import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle2, Upload, FileText, AlertCircle, Loader2, Download, PenTool, X, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { apiService } from '@/lib/api';

function UploadForms() {
  const { uploadToken } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState(null);
  const [attachedDocuments, setAttachedDocuments] = useState([]);
  const [uploadedForms, setUploadedForms] = useState({});
  const [signedForms, setSignedForms] = useState({});
  const [uploading, setUploading] = useState({});
  const [activeSignature, setActiveSignature] = useState(null);
  const [expandedSections, setExpandedSections] = useState({ download: true, sign: true, upload: true });
  const [coiExpiration, setCoiExpiration] = useState('');
  
  // Editable lien waiver form fields
  const [lienWaiverData, setLienWaiverData] = useState({
    claimant_name: '',
    claimant_address: '',
    waiver_amount: '',
    through_date: new Date().toISOString().split('T')[0],
    work_description: ''
  });
  
  // Signature canvas refs
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);

  useEffect(() => {
    loadFormRequest();
  }, [uploadToken]);

  const loadFormRequest = async () => {
    try {
      console.log('Loading form request with token:', uploadToken);
      const [formResponse, docsResponse] = await Promise.all([
        apiService.getFormRequestByToken(uploadToken),
        apiService.getFormRequestDocuments(uploadToken).catch(() => ({ data: [] }))
      ]);
      
      console.log('Form response:', formResponse.data);
      setFormData(formResponse.data);
      setAttachedDocuments(docsResponse.data || []);
      
      // Track which forms are already uploaded or signed
      const uploaded = {};
      const signed = {};
      formResponse.data.requests.forEach(req => {
        if (req.status === 'received') {
          uploaded[req.form_type] = true;
        }
        if (req.signature_data) {
          signed[req.form_type] = true;
        }
      });
      setUploadedForms(uploaded);
      setSignedForms(signed);
      
      // Pre-fill lien waiver data from request
      const lienWaiverRequest = formResponse.data.requests.find(r => r.form_type === 'lien_waiver');
      const contractor = formResponse.data.contractor;
      if (lienWaiverRequest || contractor) {
        setLienWaiverData({
          claimant_name: contractor?.company || contractor?.name || '',
          claimant_address: '',
          waiver_amount: lienWaiverRequest?.lien_waiver_amount || '',
          through_date: lienWaiverRequest?.through_date || new Date().toISOString().split('T')[0],
          work_description: lienWaiverRequest?.phase_name || ''
        });
      }
    } catch (err) {
      console.error('Error loading form request:', err);
      console.error('Token used:', uploadToken);
      console.error('Response:', err.response?.data);
      if (err.response?.status === 410) {
        setError('This form request has expired. Please contact the project manager for a new link.');
      } else if (err.response?.status === 404) {
        setError(`Form request not found. Token: ${uploadToken?.substring(0, 8)}... Please contact the sender for a new link.`);
      } else {
        setError(`Failed to load form request: ${err.message}. Please try again later.`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileUpload = async (formType, e) => {
    const file = e.target.files[0];
    if (!file) return;

    // v2.6: COI requires an expiration date so insurance-lapse tracking works.
    if (formType === 'coi' && !coiExpiration) {
      toast.error('Please enter the COI expiration date before uploading.');
      e.target.value = '';
      return;
    }

    setUploading(prev => ({ ...prev, [formType]: true }));

    try {
      await apiService.uploadFormDocument(uploadToken, formType, file, formType === 'coi' ? coiExpiration : undefined);
      toast.success(`${getFormTypeLabel(formType)} uploaded successfully!`);
      setUploadedForms(prev => ({ ...prev, [formType]: true }));
    } catch (err) {
      console.error('Upload error:', err);
      toast.error(err?.response?.data?.detail || 'Failed to upload file. Please try again.');
    } finally {
      setUploading(prev => ({ ...prev, [formType]: false }));
    }
  };

  const getFormTypeLabel = (formType) => {
    const labels = {
      w9: 'W-9 Tax Form',
      lien_waiver: 'Lien Waiver',
      coi: 'Certificate of Insurance'
    };
    return labels[formType] || formType;
  };

  const getFormTypeDescription = (formType) => {
    const descriptions = {
      w9: 'IRS Form W-9 for tax reporting purposes',
      lien_waiver: 'Signed lien waiver document',
      coi: 'Current certificate of insurance coverage'
    };
    return descriptions[formType] || '';
  };

  // Signature canvas functions
  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches[0].clientX) - rect.left;
    const y = (e.clientY || e.touches[0].clientY) - rect.top;
    
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches[0].clientX) - rect.left;
    const y = (e.clientY || e.touches[0].clientY) - rect.top;
    
    ctx.lineTo(x, y);
    ctx.strokeStyle = '#1e40af';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  const submitSignature = async (formType) => {
    const canvas = canvasRef.current;
    const signatureData = canvas.toDataURL('image/png');
    
    // Check if signature is empty
    const ctx = canvas.getContext('2d');
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const hasContent = imageData.data.some((pixel, i) => i % 4 === 3 && pixel > 0);
    
    if (!hasContent) {
      toast.error('Please sign before submitting');
      return;
    }
    
    try {
      // Include lien waiver form data if it's a lien waiver
      const formFields = formType === 'lien_waiver' ? {
        claimant_name: lienWaiverData.claimant_name,
        claimant_address: lienWaiverData.claimant_address,
        waiver_amount: lienWaiverData.waiver_amount ? parseFloat(lienWaiverData.waiver_amount) : null,
        through_date: lienWaiverData.through_date,
        work_description: lienWaiverData.work_description
      } : {};
      
      await apiService.submitESignature(uploadToken, formType, signatureData, formFields);
      toast.success(`${getFormTypeLabel(formType)} signed successfully!`);
      setSignedForms(prev => ({ ...prev, [formType]: true }));
      setActiveSignature(null);
    } catch (err) {
      console.error('Signature error:', err);
      toast.error('Failed to submit signature. Please try again.');
    }
  };

  const downloadDocument = async (doc) => {
    try {
      const response = await apiService.downloadDocument(doc.id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Document downloaded!');
    } catch (err) {
      console.error('Download error:', err);
      toast.error('Failed to download document');
    }
  };

  const downloadLienWaiverPdf = async () => {
    try {
      toast.info('Generating lien waiver PDF...');
      const response = await apiService.downloadLienWaiverPdf(uploadToken);
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      const contractorName = formData?.contractor?.company || formData?.contractor?.name || 'contractor';
      link.setAttribute('download', `Lien_Waiver_${contractorName.replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Lien waiver downloaded! Print, sign, and upload below.');
    } catch (err) {
      console.error('Download lien waiver error:', err);
      toast.error('Failed to download lien waiver');
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-slate-600">Loading document portal...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-800 mb-2">Unable to Load</h2>
            <p className="text-slate-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const requestedForms = formData?.requests || [];
  const notes = requestedForms[0]?.notes;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4 sm:p-6">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <Card className="mb-6 bg-gradient-to-r from-blue-600 to-blue-700 text-white border-none shadow-xl">
          <CardHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                <CheckCircle2 className="w-7 h-7" />
              </div>
              <div>
                <CardTitle className="text-xl">DrawReady Document Portal</CardTitle>
                <CardDescription className="text-blue-100">
                  Secure document exchange
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {formData?.contractor && (
              <div className="bg-white/10 rounded-lg p-4">
                <p className="text-sm text-blue-100 mb-1">Welcome,</p>
                <p className="font-semibold text-lg">{formData.contractor.name}</p>
                {formData.contractor.company && (
                  <p className="text-blue-200 text-sm">{formData.contractor.company}</p>
                )}
                {formData.project && (
                  <p className="text-blue-100 text-sm mt-2">
                    <span className="opacity-70">Project:</span> {formData.project.address || formData.project.name}
                  </p>
                )}
              </div>
            )}
            {notes && (
              <div className="mt-4 bg-yellow-500/20 rounded-lg p-3 border border-yellow-400/30">
                <p className="text-sm font-medium text-yellow-100">📝 Instructions:</p>
                <p className="text-sm text-yellow-50 mt-1">{notes}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Downloads Section - Documents FROM project owner */}
        {attachedDocuments.length > 0 && (
          <Card className="mb-4">
            <CardHeader 
              className="cursor-pointer hover:bg-slate-50 transition-colors"
              onClick={() => toggleSection('download')}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <Download className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">Documents for You</CardTitle>
                    <CardDescription>{attachedDocuments.length} document(s) from the project owner - download for your records</CardDescription>
                  </div>
                </div>
                {expandedSections.download ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
              </div>
            </CardHeader>
            {expandedSections.download && (
              <CardContent>
                <p className="text-sm text-slate-600 mb-3">📥 These are documents sent by the project owner for your reference. Download and keep for your records.</p>
                <div className="space-y-3">
                  {attachedDocuments.map((doc) => (
                    <div key={doc.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-blue-600" />
                        <div>
                          <p className="font-medium text-slate-800">{doc.name || doc.file_name || 'Document'}</p>
                          <p className="text-xs text-slate-500">{doc.category || 'Document'}</p>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => downloadDocument(doc)}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <Download className="w-4 h-4 mr-1" />
                        Download
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            )}
          </Card>
        )}

        {/* E-Sign Section */}
        {requestedForms.some(r => r.form_type === 'lien_waiver') && (
          <Card className="mb-4">
            <CardHeader 
              className="cursor-pointer hover:bg-slate-50 transition-colors"
              onClick={() => toggleSection('sign')}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                    <PenTool className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">E-Sign Lien Waiver</CardTitle>
                    <CardDescription>Complete and sign the lien waiver electronically</CardDescription>
                  </div>
                </div>
                {expandedSections.sign ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
              </div>
            </CardHeader>
            {expandedSections.sign && (
              <CardContent>
                <div className="space-y-4">
                  {requestedForms.filter(r => r.form_type === 'lien_waiver').map((request) => (
                    <div key={request.id} className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <h4 className="font-medium">{getFormTypeLabel(request.form_type)}</h4>
                          <p className="text-sm text-slate-500">{getFormTypeDescription(request.form_type)}</p>
                        </div>
                        {signedForms[request.form_type] && (
                          <span className="flex items-center gap-1 text-green-600 text-sm font-medium">
                            <CheckCircle2 className="w-4 h-4" /> Signed
                          </span>
                        )}
                      </div>

                      {!signedForms[request.form_type] && (
                        <div className="mt-4 space-y-4">
                          {/* Project Info (Read Only) */}
                          <div className="p-3 bg-blue-50 rounded-lg">
                            <h5 className="font-medium text-blue-900 mb-2">Project Information</h5>
                            <div className="grid grid-cols-1 gap-1 text-sm text-blue-800">
                              <p><span className="font-medium">Project:</span> {formData?.project?.name || 'N/A'}</p>
                              <p><span className="font-medium">Address:</span> {formData?.project?.address || 'N/A'}</p>
                              <p><span className="font-medium">Owner:</span> {formData?.project?.borrower || 'N/A'}</p>
                            </div>
                          </div>
                          
                          {/* Editable Claimant Info */}
                          <div className="space-y-3">
                            <h5 className="font-medium text-slate-900">Claimant Information</h5>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">Claimant Name / Company *</label>
                                <input
                                  type="text"
                                  value={lienWaiverData.claimant_name}
                                  onChange={(e) => setLienWaiverData(prev => ({ ...prev, claimant_name: e.target.value }))}
                                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                  placeholder="Your company or name"
                                  required
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">Claimant Address</label>
                                <input
                                  type="text"
                                  value={lienWaiverData.claimant_address}
                                  onChange={(e) => setLienWaiverData(prev => ({ ...prev, claimant_address: e.target.value }))}
                                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                  placeholder="Street, City, State, ZIP"
                                />
                              </div>
                            </div>
                          </div>
                          
                          {/* Editable Waiver Details */}
                          <div className="space-y-3">
                            <h5 className="font-medium text-slate-900">Waiver Details</h5>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                              <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">Work Description</label>
                                <input
                                  type="text"
                                  value={lienWaiverData.work_description}
                                  onChange={(e) => setLienWaiverData(prev => ({ ...prev, work_description: e.target.value }))}
                                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                  placeholder="Type of work performed"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">Amount ($) *</label>
                                <input
                                  type="number"
                                  value={lienWaiverData.waiver_amount}
                                  onChange={(e) => setLienWaiverData(prev => ({ ...prev, waiver_amount: e.target.value }))}
                                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                  placeholder="0.00"
                                  step="0.01"
                                  required
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">Through Date *</label>
                                <input
                                  type="date"
                                  value={lienWaiverData.through_date}
                                  onChange={(e) => setLienWaiverData(prev => ({ ...prev, through_date: e.target.value }))}
                                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                                  required
                                />
                              </div>
                            </div>
                          </div>
                          
                          {/* Waiver Terms */}
                          <div className="p-3 bg-slate-100 rounded-lg text-xs text-slate-600 leading-relaxed">
                            <p className="mb-2">
                              The undersigned, for and in consideration of the payment of the above amount, receipt of which is hereby 
                              acknowledged, does hereby waive and release any and all lien or claim of lien, under the Constitution and 
                              statutes of the State, on account of labor or materials, or both, furnished by the undersigned to or for 
                              the above described property through the date shown above.
                            </p>
                            <p>
                              The undersigned further certifies that all bills for labor and materials furnished by the undersigned have 
                              been paid in full and that no claims or liens exist against the property on account of materials or labor 
                              furnished by the undersigned.
                            </p>
                          </div>
                          
                          {/* Signature Section */}
                          <div className="p-4 bg-purple-50 rounded-lg">
                            <p className="text-sm font-medium text-purple-900 mb-3">Sign Below</p>
                            <canvas
                              ref={canvasRef}
                              width={400}
                              height={150}
                              className="border-2 border-dashed border-purple-300 rounded-lg bg-white w-full touch-none"
                              onMouseDown={startDrawing}
                              onMouseMove={draw}
                              onMouseUp={stopDrawing}
                              onMouseLeave={stopDrawing}
                              onTouchStart={startDrawing}
                              onTouchMove={draw}
                              onTouchEnd={stopDrawing}
                            />
                            <div className="flex gap-2 mt-3">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={clearSignature}
                              >
                                Clear
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => submitSignature(request.form_type)}
                                className="bg-purple-600 hover:bg-purple-700 ml-auto"
                                disabled={!lienWaiverData.claimant_name || !lienWaiverData.waiver_amount}
                              >
                                Submit Signed Waiver
                              </Button>
                            </div>
                            <p className="text-xs text-purple-600 mt-2">
                              By signing, you agree to the terms of this lien waiver document.
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                
                <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-blue-800">Prefer to sign physically?</p>
                      <p className="text-sm text-blue-600">Download, print, sign, and upload the completed document below.</p>
                    </div>
                    <Button
                      onClick={downloadLienWaiverPdf}
                      variant="outline"
                      className="border-blue-300 text-blue-700 hover:bg-blue-100"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download Lien Waiver PDF
                    </Button>
                  </div>
                </div>
              </CardContent>
            )}
          </Card>
        )}

        {/* Upload Section - Only show for non-lien-waiver forms, or lien waiver if user prefers to upload scanned copy */}
        {requestedForms.filter(r => r.form_type !== 'lien_waiver').length > 0 && (
          <Card className="mb-4">
            <CardHeader 
              className="cursor-pointer hover:bg-slate-50 transition-colors"
              onClick={() => toggleSection('upload')}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <Upload className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">Upload Documents</CardTitle>
                    <CardDescription>Submit requested documents (W-9, COI, etc.)</CardDescription>
                  </div>
                </div>
                {expandedSections.upload ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
              </div>
            </CardHeader>
            {expandedSections.upload && (
              <CardContent>
                <div className="space-y-4">
                  {requestedForms.filter(r => r.form_type !== 'lien_waiver').map((request) => (
                    <div key={request.id} className="border rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium">{getFormTypeLabel(request.form_type)}</h4>
                          <p className="text-sm text-slate-500">{getFormTypeDescription(request.form_type)}</p>
                        </div>
                        {uploadedForms[request.form_type] || signedForms[request.form_type] ? (
                          <span className="flex items-center gap-1 text-green-600 text-sm font-medium">
                            <CheckCircle2 className="w-4 h-4" /> 
                            {signedForms[request.form_type] ? 'Signed' : 'Uploaded'}
                          </span>
                        ) : (
                          <div className="relative">
                            <input
                              type="file"
                              id={`upload-${request.form_type}`}
                              className="hidden"
                              accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                              onChange={(e) => handleFileUpload(request.form_type, e)}
                              disabled={uploading[request.form_type]}
                            />
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => document.getElementById(`upload-${request.form_type}`).click()}
                              disabled={uploading[request.form_type]}
                              className="border-blue-300 text-blue-700 hover:bg-blue-50"
                              data-testid={`upload-${request.form_type}-btn`}
                            >
                              {uploading[request.form_type] ? (
                                <Loader2 className="w-4 h-4 animate-spin mr-1" />
                              ) : (
                                <Upload className="w-4 h-4 mr-1" />
                              )}
                              {uploading[request.form_type] ? 'Uploading...' : 'Upload'}
                            </Button>
                          </div>
                        )}
                      </div>
                      {/* v2.6: Per-project COI — show the additional-insured requirement
                          and capture the expiration date on the contractor side. */}
                      {request.form_type === 'coi' && !uploadedForms['coi'] && (
                        <div className="mt-3 space-y-3 border-t pt-3">
                          {request.additional_insured && (
                            <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">
                              <span className="font-medium">Additional insured required:</span> {request.additional_insured}
                              <p className="text-xs text-amber-700 mt-1">
                                Please make sure your certificate names the entity above as an additional insured.
                              </p>
                            </div>
                          )}
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">
                              Policy expiration date <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="date"
                              value={coiExpiration}
                              onChange={(e) => setCoiExpiration(e.target.value)}
                              className="w-full sm:w-56 border border-slate-300 rounded-md px-3 py-2 text-sm"
                              data-testid="coi-expiration-input"
                            />
                            <p className="text-xs text-slate-500 mt-1">Required before you upload your COI.</p>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                
                <p className="text-xs text-slate-500 mt-4">
                  Accepted formats: PDF, DOC, DOCX, PNG, JPG • Max file size: 10MB
                </p>
              </CardContent>
            )}
          </Card>
        )}

        {/* Lien Waiver Upload Option (for physical signatures) - only if lien waiver requested and NOT yet signed electronically */}
        {requestedForms.some(r => r.form_type === 'lien_waiver') && !signedForms['lien_waiver'] && !uploadedForms['lien_waiver'] && (
          <Card className="mb-4 border-slate-200">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-700">Have a physical signed copy?</p>
                  <p className="text-xs text-slate-500">Upload your scanned/photographed lien waiver here instead</p>
                </div>
                <div className="relative">
                  <input
                    type="file"
                    id="upload-lien-waiver-physical"
                    className="hidden"
                    accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                    onChange={(e) => handleFileUpload('lien_waiver', e)}
                    disabled={uploading['lien_waiver']}
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => document.getElementById('upload-lien-waiver-physical').click()}
                    disabled={uploading['lien_waiver']}
                    className="border-slate-300 text-slate-700 hover:bg-slate-50"
                  >
                    {uploading['lien_waiver'] ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-1" />
                    ) : (
                      <Upload className="w-4 h-4 mr-1" />
                    )}
                    {uploading['lien_waiver'] ? 'Uploading...' : 'Upload Signed Copy'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Completion Status */}
        {requestedForms.every(r => uploadedForms[r.form_type] || signedForms[r.form_type]) && (
          <Card className="bg-green-50 border-green-200">
            <CardContent className="pt-6 text-center">
              <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-green-800 mb-2">All Documents Submitted!</h3>
              <p className="text-green-700">Thank you for completing your document submission. You may close this page.</p>
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <div className="text-center mt-8 text-sm text-slate-500">
          <p>Powered by <span className="font-semibold text-blue-600">DrawReady</span></p>
          <p className="mt-1">The Financial Operating System for Developers</p>
        </div>
      </div>
    </div>
  );
}

export default UploadForms;
